import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { humanDate, personName, stamp } from "./format";
import { liveBuilder, RuleBuilder, type BuilderMode } from "./RuleBuilder";
import {
  cellLine, cellsQuery, deleteReplacement, deleteRule, deleteStrategy, editedStamp, editStrategy, editStrategyBody, editStrategyKey, goalLabel, metricNoData,
  movedOrder, NAME_MAX, NO_STRATEGY, reorderRules, ruleSentence, STRATEGIES_COPY as T, strategyByValue, strategyColour, type Library, type Rule, type StrategyDraft,
  type StrategyEntry,
} from "./strategies";
import { FormError, MetricSelect, NoData, SectionHead, StrategyDot, unitMark, useStrategyWrite, useWritePending, type Editor } from "./StrategyParts";

/** "Strategy definitions": v26's footnote, then one card per strategy, or the empty state with New strategy (design call R). */
export function StrategyDefinitions({ library, editor, onNew }: { library: Library; editor: Editor; onNew: () => void }) {
  return (
    <section className="flex flex-col gap-2" data-section="definitions">
      <SectionHead title={T.definitions.title} desc={T.definitions.footnote} />
      {library.strategies.length === 0
        ? <Card><CardContent className="flex flex-col items-start gap-2 py-6"><p className="font-medium text-ks-ink">{T.definitions.empty}</p><p className="text-sm text-ks-grey-1">{T.definitions.emptyHint}</p><Button size="sm" onClick={onNew}>{T.newStrategy}</Button></CardContent></Card>
        // keyed by id: a rename or goal edit re-versions the strategy to a new id, which drops any form open on the old one
        : library.strategies.map((s) => <StrategyCard key={s.id} entry={s} library={library} editor={editor} />)}
    </section>
  );
}

/**
 * One strategy (v26 strat-card): name, how many cells use it (C2's count, design call G), the last edit; Rename and Edit goal
 * (a re-version by id, design call F); Add rule and Delete strategy; the rules by stored priority (dense 1..n, design call C)
 * with Up, Down, Edit and Delete; the goal last. Up and Down send the whole new order; the browser never works out a priority.
 * `initialBuilder` renders a builder already open (a node test cannot click).
 */
export function StrategyCard({ entry, library, editor, initialBuilder = null }: { entry: StrategyEntry; library: Library; editor: Editor; initialBuilder?: BuilderMode | null }) {
  const client = useQueryClient();
  const [builder, setBuilder] = useState<BuilderMode | null>(initialBuilder);
  const [form, setForm] = useState<"name" | "goal" | null>(null);
  const [deleting, setDeleting] = useState(false);
  const move = useStrategyWrite(editor, (ids: number[]) => reorderRules(entry.id, ids), (a) => (a.unchanged ? T.done.unchanged : T.done.reordered(a.name)));
  const drop = useStrategyWrite(editor, (rule: Rule) => deleteRule(entry.id, rule.id), (a, rule) => T.done.ruleDeleted(rule.priority, ruleSentence(rule, library.metrics), a.name));
  // Closed, and its cells read dropped, before the refresh: the strategy is gone, so a refetch of its cells would only 404.
  const remove = useStrategyWrite(editor, (replacement: number | typeof NO_STRATEGY | undefined) => deleteStrategy(entry.id, replacement), (a) => T.done.deleted(entry.name, a),
    () => { setDeleting(false); client.removeQueries({ queryKey: cellsQuery(entry.id).queryKey }); });
  // An edit builder names its rule by id, and a move or delete renumbers by new ids: while one is open the card's own Up, Down and
  // Delete wait, and once its rule is gone (moved or edited elsewhere) it closes. A write stays pending until the refetch lands,
  // so nothing here is clicked against the old ids. A Rename or Edit goal re-versions the strategy itself to a new id: while its
  // save is pending (read from the cache, so closing its form does not release it) Up, Down, Edit and Delete wait, and Add rule
  // and Delete strategy too.
  const open = liveBuilder(builder, entry.rules);
  const saving = useWritePending(editStrategyKey(entry.id));
  const writing = move.isPending || drop.isPending || saving;
  const busy = writing || open?.kind === "edit";
  const last = entry.rules.length - 1;
  const edited = editedStamp(entry);
  return (
    <Card data-strategy={entry.name}><CardContent className="flex flex-col gap-3 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <StrategyDot colour={strategyColour(library.strategies, entry.name)} />
        <span className="text-base font-semibold text-ks-ink">{entry.name}</span>
        <Badge variant="secondary">{T.definitions.assigned(entry.assignment_count)}</Badge>
        {entry.assigned_retired_skus.length > 0 && <Badge variant="outline">{T.definitions.onRetired(entry.assigned_retired_skus)}</Badge>}
        <span className="text-xs text-ks-grey-1">{entry.edited_by ? T.definitions.editedBy(personName(edited.by), humanDate(edited.at)) : T.definitions.setBy(personName(entry.set_by), stamp(entry.set_at), entry.source)}</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" variant="outline" aria-label={T.definitions.forStrategy(T.definitions.rename, entry.name)} onClick={() => setForm("name")}>{T.definitions.rename}</Button>
          <Button size="sm" variant="outline" aria-label={T.definitions.forStrategy(T.definitions.editGoal, entry.name)} onClick={() => setForm("goal")}>{T.definitions.editGoal}</Button>
          <Button size="sm" aria-label={T.definitions.forStrategy(T.definitions.addRule, entry.name)} disabled={saving} onClick={() => setBuilder({ kind: "add", strategy: entry })}>{T.definitions.addRule}</Button>
          <Button size="sm" variant="destructive" aria-label={T.definitions.forStrategy(T.definitions.deleteStrategy, entry.name)} disabled={saving} onClick={() => setDeleting(true)}>{T.definitions.deleteStrategy}</Button>
        </div>
      </div>
      {form && <StrategyEditForm entry={entry} part={form} library={library} editor={editor} onClose={() => setForm(null)} />}
      <Table>
        <TableHeader><TableRow>{T.definitions.columns.map((c) => <TableHead key={c} className={c === T.definitions.columns[0] ? "w-20" : undefined}>{c}</TableHead>)}<TableHead><span className="sr-only">{T.definitions.actions}</span></TableHead></TableRow></TableHeader>
        <TableBody>
          {entry.rules.length === 0 && <TableRow><TableCell colSpan={3} className="text-ks-grey-1">{T.definitions.noRulesAdd}</TableCell></TableRow>}
          {entry.rules.map((r, i) => (
            <TableRow key={r.id}>
              <TableCell className="tabular-nums">{r.priority}</TableCell>
              <TableCell className="whitespace-normal">{ruleSentence(r, library.metrics)}<NoData reason={metricNoData(library.metrics, r.metric)} /></TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" aria-label={T.definitions.moveUp} disabled={i === 0 || busy} onClick={() => { const ids = movedOrder(entry.rules, r.id, -1); if (ids) move.mutate(ids); }}>{T.definitions.up}</Button>
                  <Button size="sm" variant="outline" aria-label={T.definitions.moveDown} disabled={i === last || busy} onClick={() => { const ids = movedOrder(entry.rules, r.id, 1); if (ids) move.mutate(ids); }}>{T.definitions.down}</Button>
                  <Button size="sm" variant="outline" aria-label={T.definitions.editRule(r.priority)} disabled={writing} onClick={() => setBuilder({ kind: "edit", strategy: entry, rule: r })}>{T.definitions.edit}</Button>
                  <Button size="sm" variant="destructive" aria-label={T.definitions.deleteRule(r.priority)} disabled={busy} onClick={() => drop.mutate(r)}>{T.definitions.delete}</Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell className="text-ks-grey-1">{T.definitions.goal}</TableCell>
            <TableCell colSpan={2} className="whitespace-normal"><span className="font-medium text-ks-ink">{goalLabel(entry, library.metrics)}</span>{entry.goal && <span className="text-ks-grey-1"> {T.definitions.goalNote}</span>}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {open && (
        <RuleBuilder key={open.kind === "edit" ? `edit-${open.rule.id}` : open.kind} mode={open} library={library} editor={editor} onClose={() => setBuilder(null)}
          onCreateNew={() => setBuilder({ kind: "template", strategy: entry })} />
      )}
      {deleting && editor.anchor && (
        <DeleteStrategyDialog entry={entry} strategies={library.strategies} container={editor.anchor} pending={remove.isPending}
          onConfirm={(replacement) => remove.mutate(replacement)} onClose={() => setDeleting(false)} />
      )}
    </CardContent></Card>
  );
}

/** Rename, or the goal (metric, "as close to", target): only what changed goes to C2, and nothing when nothing changed. The save
 * carries `editStrategyKey`, which its card reads to hold its writes on the old id. */
export function StrategyEditForm({ entry, part, library, editor, onClose }: { entry: StrategyEntry; part: "name" | "goal"; library: Library; editor: Editor; onClose: () => void }) {
  const [draft, setDraft] = useState<StrategyDraft>({ name: entry.name, goal_metric: entry.goal?.metric ?? "", goal_target: entry.goal?.target ?? "" });
  const [error, setError] = useState<string | null>(null);
  const save = useStrategyWrite(editor, (body: Parameters<typeof editStrategy>[1]) => editStrategy(entry.id, body),
    (a, body) => (a.unchanged ? T.done.unchanged : body.name ? T.done.renamed(entry.name, a.name) : T.done.goal(a.name)), undefined, editStrategyKey(entry.id));
  const submit = () => {
    const checked = editStrategyBody(entry, draft, library.strategies);
    if (!checked.ok) { setError(checked.error); return; }
    setError(null);
    if (!checked.body) { editor.report({ tone: "ok", text: T.done.unchanged }); onClose(); return; }
    save.mutate(checked.body, { onSuccess: onClose });
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-ks-grey-4 p-3" data-form={part}>
      <div className="flex flex-wrap items-center gap-2">
        {part === "name" ? (
          <>
            <span className="text-xs text-ks-grey-1">{T.create.name}</span>
            <Input className="w-64" aria-label={T.create.name} maxLength={NAME_MAX} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </>
        ) : (
          <>
            <span className="text-xs text-ks-grey-1">{T.create.goal}</span>
            <MetricSelect metrics={library.metrics} value={draft.goal_metric} onChange={(goal_metric) => setDraft({ ...draft, goal_metric })} label={T.create.goal} placeholder={T.builder.pickMetric} />
            <span className="text-sm text-ks-grey-1">{T.create.asCloseTo}</span>
            <Input className="w-20" inputMode="decimal" aria-label={T.create.target} value={draft.goal_target} onChange={(e) => setDraft({ ...draft, goal_target: e.target.value })} />
            <span className="text-sm text-ks-grey-1">{unitMark(library.metrics, draft.goal_metric)}</span>
          </>
        )}
        <Button size="sm" onClick={submit} disabled={save.isPending}>{T.definitions.save}</Button>
        <Button size="sm" variant="outline" data-nonmutating onClick={onClose}>{T.definitions.cancel}</Button>
      </div>
      <FormError error={error} />
    </div>
  );
}

/**
 * v26's delete modal, both versions. Not assigned: a confirm. Assigned: the cells C2 lists for it (a retired SKU's marked as cleared
 * on delete), and a replacement to choose, or No strategy (design call F). Mounted in the tab's in-gate anchor. The card owns the
 * write; the dialog only reads the cells and hands back the choice.
 */
export function DeleteStrategyDialog({ entry, strategies, container, pending, onConfirm, onClose }: {
  entry: StrategyEntry; strategies: StrategyEntry[]; container: HTMLElement; pending: boolean;
  onConfirm: (replacement: number | typeof NO_STRATEGY | undefined) => void; onClose: () => void;
}) {
  const assigned = entry.assignment_count > 0;
  const cells = useQuery({ ...cellsQuery(entry.id), enabled: assigned });
  const [choice, setChoice] = useState("");
  const [error, setError] = useState<string | null>(null);
  const others = strategies.filter((s) => s.id !== entry.id);
  const confirm = () => {
    // Not assigned on an active SKU: a plain delete, or `none` when only retired SKUs still carry it (C2 clears those first). A
    // replacement the refreshed library no longer holds is asked for again, never sent as a plain delete.
    const checked = deleteReplacement(entry, others, choice);
    if (!checked.ok) { setChoice(""); setError(checked.error); return; }
    onConfirm(checked.body);
  };
  const picked = choice === NO_STRATEGY ? T.remove.noStrategy : strategyByValue(others, choice)?.name;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent container={container} className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{T.remove.title(entry.name)}</DialogTitle>
          <DialogDescription className="text-ks-ink">{assigned ? T.remove.assigned(entry.name, entry.assignment_count, entry.assigned_retired_skus.length) : T.remove.unassigned}</DialogDescription>
        </DialogHeader>
        {assigned && (
          <div className="flex flex-col gap-3">
            {cells.isPending && <p role="status" className="text-sm text-ks-grey-1">{T.remove.reading}</p>}
            {cells.isError && <p role="alert" className="text-sm">{T.remove.unread}</p>}
            {cells.data && <ul className="max-h-60 list-disc overflow-y-auto pl-5 text-sm">{cells.data.map((c) => <li key={`${c.xero_sku}|${c.marketplace_id}`}>{cellLine(c)}</li>)}</ul>}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">{T.remove.replacement}</span>
              <Select value={choice} onValueChange={(v) => { setChoice(v); setError(null); }}>
                <SelectTrigger className="w-60" aria-label={T.remove.replacement}><SelectValue placeholder={T.remove.choose}>{picked}</SelectValue></SelectTrigger>
                <SelectContent>
                  {others.map((s) => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                  <SelectItem value={NO_STRATEGY}>{T.remove.noStrategy}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FormError error={error} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" data-nonmutating onClick={onClose}>{T.remove.cancel}</Button>
          <Button variant="destructive" disabled={pending || (assigned && !cells.data)} onClick={confirm}>{assigned ? T.remove.replaceAndDelete : T.remove.confirm}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
